import { useEffect, useState, useRef } from 'react';
import { motion, useMotionValue, useSpring } from 'motion/react';

export default function CustomCursor() {
  const [cursorType, setCursorType] = useState<string>('default');
  const [cursorText, setCursorText] = useState<string>('');
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const [isMouseDown, setIsMouseDown] = useState<boolean>(false);
  const [isMobileDevice, setIsMobileDevice] = useState<boolean>(false);
  
  const cursorX = useMotionValue(-100);
  const cursorY = useMotionValue(-100);
  
  const springConfig = { damping: 30, stiffness: 350, mass: 0.6 };
  const cursorSpringX = useSpring(cursorX, springConfig);
  const cursorSpringY = useSpring(cursorY, springConfig);
  
  // Secondary trailing coordinates for elastic outer circular halo
  const trailX = useMotionValue(-100);
  const trailY = useMotionValue(-100);
  const trailSpringX = useSpring(trailX, { damping: 24, stiffness: 150, mass: 0.8 });
  const trailSpringY = useSpring(trailY, { damping: 24, stiffness: 150, mass: 0.8 });
  
  const lastMousePos = useRef({ x: 0, y: 0 });
  const [stretchScale, setStretchScale] = useState({ x: 1, y: 1 });
  const [rotateAngle, setRotateAngle] = useState(0);
  const [velocity, setVelocity] = useState(0);

  useEffect(() => {
    const checkMobile = () => {
      // Screen-width check (under 768px is mobile) rather than touchscreen flag,
      // to support hybrid touchscreen laptops/tablets with a cursor
      setIsMobileDevice(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    const handleMouseMove = (e: MouseEvent) => {
      const { clientX, clientY } = e;
      cursorX.set(clientX);
      cursorY.set(clientY);
      trailX.set(clientX);
      trailY.set(clientY);
      
      if (!isVisible) setIsVisible(true);
      
      // Calculate cursor stretching based on mouse speed
      const dx = clientX - lastMousePos.current.x;
      const dy = clientY - lastMousePos.current.y;
      
      const speed = Math.sqrt(dx * dx + dy * dy);
      setVelocity(speed);
      const angle = Math.atan2(dy, dx) * (180 / Math.PI);
      
      // Limit speed stretch factor
      const maxStretch = 1.4;
      const stretchX = 1 + Math.min(speed * 0.006, maxStretch - 1);
      const stretchY = 1 - Math.min(speed * 0.003, 0.3);
      
      setStretchScale({ x: stretchX, y: stretchY });
      setRotateAngle(angle);
      
      lastMousePos.current = { x: clientX, y: clientY };
    };

    const handleMouseLeave = () => {
      setIsVisible(false);
    };

    const handleMouseDown = () => setIsMouseDown(true);
    const handleMouseUp = () => setIsMouseDown(false);

    const handleMouseOver = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target) return;

      const hoverElement = target.closest('[data-cursor]');
      if (hoverElement) {
        const type = hoverElement.getAttribute('data-cursor') || 'hover';
        setCursorType(type);
        const text = hoverElement.getAttribute('data-cursor-text') || '';
        setCursorText(text);
      } else {
        const isClickable = target.closest('a, button, input, select, textarea, [role="button"]');
        if (isClickable) {
          setCursorType('hover');
        } else {
          setCursorType('default');
        }
        setCursorText('');
      }
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mousedown', handleMouseDown);
    window.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('mouseleave', handleMouseLeave);
    document.addEventListener('mouseover', handleMouseOver);

    return () => {
      window.removeEventListener('resize', checkMobile);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mousedown', handleMouseDown);
      window.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('mouseleave', handleMouseLeave);
      document.removeEventListener('mouseover', handleMouseOver);
    };
  }, [cursorX, cursorY, trailX, trailY, isVisible, isMobileDevice]);

  if (isMobileDevice || !isVisible) return null;

  // Render various cursor visual types
  const getCursorStyles = () => {
    switch (cursorType) {
      case 'hover':
        return {
          width: 54,
          height: 54,
          backgroundColor: 'rgba(34, 197, 94, 0.15)',
          borderColor: '#22c55e',
          borderWidth: 2,
          borderRadius: '50%',
        };
      case 'view':
        return {
          width: 80,
          height: 80,
          backgroundColor: '#22c55e',
          borderColor: '#22c55e',
          borderWidth: 0,
          borderRadius: '50%',
        };
      case 'close':
        return {
          width: 60,
          height: 60,
          backgroundColor: '#ef4444',
          borderColor: '#ef4444',
          borderWidth: 0,
          borderRadius: '50%',
        };
      case 'drag':
        return {
          width: 70,
          height: 70,
          backgroundColor: 'rgba(255, 255, 255, 0.1)',
          borderColor: '#9ca3af',
          borderWidth: 1.5,
          borderRadius: '50%',
        };
      case 'default':
      default:
        return {
          width: 24,
          height: 24,
          backgroundColor: 'rgba(34, 197, 94, 0.16)', // beautiful glow
          borderColor: '#22c55e',                     // premium sharp emerald border
          borderWidth: 1.5,
          borderRadius: '50%',
        };
    }
  };

  const cursorStyle = getCursorStyles();

  return (
    <>
      {/* Elastic Trailing Circle - Floating behind default cursor */}
      {cursorType === 'default' && (
        <motion.div
          className="fixed top-0 left-0 pointer-events-none rounded-full border border-emerald-500/25 dark:border-emerald-400/20 bg-emerald-500/10 dark:bg-emerald-400/8"
          style={{
            zIndex: 99999,
            x: trailSpringX,
            y: trailSpringY,
            translateX: '-50%',
            translateY: '-50%',
            // Circle width/height stretches elegantly as the user slides across the screen
            width: 38 + Math.min(velocity * 0.45, 38),
            height: 38 + Math.min(velocity * 0.45, 38),
          }}
          animate={{
            scale: isMouseDown ? 0.65 : 1,
            // Apply slight rotation based on cursor movement angle
            rotate: velocity > 3 ? rotateAngle : 0,
          }}
          transition={{
            type: 'spring',
            stiffness: 180,
            damping: 18,
          }}
        />
      )}

      {/* Lagging main outer cursor ring */}
      <motion.div
        className="fixed top-0 left-0 pointer-events-none flex items-center justify-center font-mono text-[9px] font-bold text-white uppercase select-none leading-none tracking-widest"
        style={{
          zIndex: 99999,
          x: cursorSpringX,
          y: cursorSpringY,
          translateX: '-50%',
          translateY: '-50%',
          ...cursorStyle,
          scaleX: cursorType === 'default' ? stretchScale.x : 1,
          scaleY: cursorType === 'default' ? stretchScale.y : 1,
          rotate: cursorType === 'default' ? `${rotateAngle}deg` : '0deg',
        }}
        animate={{
          scale: isMouseDown ? (cursorType === 'default' ? 0.75 : 0.85) : 1,
        }}
        transition={{ type: 'spring', stiffness: 450, damping: 24 }}
      >
        {cursorType === 'view' && (
          <motion.span initial={{ opacity: 0, scale: 0.6 }} animate={{ opacity: 1, scale: 1 }} className="text-white font-semibold">
            {cursorText || 'VIEW'}
          </motion.span>
        )}
        {cursorType === 'close' && (
          <motion.span initial={{ opacity: 0, scale: 0.6 }} animate={{ opacity: 1, scale: 1 }} className="text-white font-semibold flex items-center justify-center">
            ✕
          </motion.span>
        )}
        {cursorType === 'drag' && (
          <motion.span initial={{ opacity: 0, scale: 0.6 }} animate={{ opacity: 1, scale: 1 }} className="text-neutral-300 font-semibold text-[8px]">
            DRAG
          </motion.span>
        )}
      </motion.div>

      {/* Tiny inner center pixel dot for default pointer */}
      {cursorType === 'default' && (
        <motion.div
          className="fixed top-0 left-0 pointer-events-none w-2 h-2 bg-[#22c55e] rounded-full shadow-sm shadow-emerald-500/50"
          style={{
            zIndex: 99999,
            x: cursorX,
            y: cursorY,
            translateX: '-50%',
            translateY: '-50%',
          }}
          animate={{
            scale: isMouseDown ? 0.5 : 1,
          }}
        />
      )}
    </>
  );
}
